import React from "react";
import { Navigate } from "react-router-dom";
import { AccessGate } from "./AccessGate";
import { useAccess } from "./AccessProvider";
import { CapabilityKey } from "./capabilities";

/** Where a route sends a user who may not open it. Guarded by no capability, so no loop. */
export const CAPABILITY_FALLBACK_PATH = "/home";

/**
 * Renders a route only for a user who may do `capability` in the organisation the session acts
 * in, from `GET api/me`, and sends anyone else to the home board.
 *
 * A refusal is decided only on an answer in use (`ready` or `refreshing`, with no refused
 * switch). In every other state nothing is known about the user, and the access gate is
 * rendered instead: the wait while loading, the failed and unusable screens, and the refused
 * switch's screen. Redirecting then would send a manager off a page because a read had not
 * arrived. PrivateRoute already renders the gate around every route, so this is a second check
 * that does not depend on the first.
 *
 * After a refused switch the gate lets the org picker through, because choosing again is the
 * way out. Its `switchOrganization` guard goes the same way, through the gate, and never reads
 * the refused answer's empty map as "may not switch".
 */
export const CapabilityRoute = ({
  capability,
  children,
}: {
  capability: CapabilityKey;
  children: React.ReactElement;
}) => {
  const access = useAccess();
  const inUse = access.status === "ready" || access.status === "refreshing";

  if (!inUse || access.switchFailed) {
    return <AccessGate>{children}</AccessGate>;
  }
  if (!access.can(capability)) {
    return <Navigate to={CAPABILITY_FALLBACK_PATH} replace />;
  }
  return children;
};
